import React, { useState, useEffect, useCallback } from 'react';
import { Button, Form } from 'react-bootstrap';
import Accordion from '../../../../components/Accordion';
import EvaluacionRiesgosPersonales from './EvaluacionRiesgosPersonales';
import IdentificacionPeligrosEntorno from './IdentificacionPeligrosEntorno';
import { useSolicitud } from '../../../../hooks/useSolicitud';
import { useSolicitudPermissions } from '../../../../hooks/useSolicitudPermissions';
import { useAnalisisRiesgo } from '../../../../hooks/useAnalisisRiesgo';
import { findAll, update } from '../../../../services/Service';
import { toast } from 'react-toastify';
import { logger } from '../../../../utils/logger';
import {
  REVISION_STATUS_ID,
  PRE_APROBADA_STATUS_ID,
  APROBADA_RT_STATUS_ID,
  APROBADA_STATUS_ID,
} from '../../../../constants/constants';

type AnalisisRiesgoProps = {
  isDisabled?: boolean;
  isEdit?: boolean;
};

const AnalisisRiesgo: React.FC<AnalisisRiesgoProps> = ({
  isDisabled = false,
  isEdit = true,
}) => {
  const [cargarAnalisisRiesgo, setCargarAnalisisRiesgo] =
    useState(true);
  const [isGuardando, setIsGuardando] = useState(false);
  const [isGuardandoComentario, setIsGuardandoComentario] =
    useState(false);
  const [observaciones, setObservaciones] = useState('');
  const [comentarioAnalistaSeguridad, setComentarioAnalistaSeguridad] =
    useState('');
  const [solicitudSeleccionada, setSolicitudSeleccionada] =
    useState('');
  const [solicitudesAprobadas, setSolicitudesAprobadas] = useState<
    Array<{ id: number; numeroSolicitud: string }>
  >([]);
  const [loadingSolicitudes, setLoadingSolicitudes] = useState(false);

  // Referencia para mantener la función de validación de IdentificacionPeligrosEntorno
  const validateEntornoRef = React.useRef<(() => boolean) | null>(
    null
  );

  // Función para establecer la función de validación
  const setValidateEntorno = React.useCallback(
    (fn: () => boolean) => {
      validateEntornoRef.current = fn;
    },
    []
  );

  const { solicitudSelected, buscarSolicitud } = useSolicitud();
  const {
    guardarAnalisisRiesgo,
    cargarAnalisisRiesgoCompleto,
    fasesPersonales,
    fasesEntorno,
    setFasesEntorno,
    agregarFase,
    editarFase,
    agregarPeligro,
    editarPeligro,
    eliminarPeligroById,
    handlePeligroSelected,
    peligroSelected,
    actualizarRiesgoEvaluacion,
    peligroCheckStatesEntorno,
    setPeligroCheckStatesEntorno,
    eliminarFase,
  } = useAnalisisRiesgo();

  const { isEmpresaGestora, isContratista, esAnalistaSeguridad } =
    useSolicitudPermissions({
      solicitudEstadoId: solicitudSelected.idTipoEstado,
    });

  // CU55: el Analista de Seguridad edita el comentario en su instancia (APROBADA_RT o PRE_APROBADA)
  // y solo cuando se ingresa en modo edición (no en visualización / action=view)
  const canEditComentarioAnalista =
    esAnalistaSeguridad &&
    isEdit &&
    (solicitudSelected.idTipoEstado === APROBADA_RT_STATUS_ID ||
      solicitudSelected.idTipoEstado === PRE_APROBADA_STATUS_ID);

  // Determinar si puede editar el análisis de riesgo
  // En estados REVISION, PRE_APROBADA, APROBADA_RT y APROBADA, Empresa Gestora NO puede editar
  // En estado APROBADA y EN_ACTUALIZACION, Contratista NO puede editar
  const canEditAnalisisRiesgo = React.useMemo(() => {
    // CU55: el Analista de Seguridad puede editar el AR en PRE_APROBADA / APROBADA_RT
    // (mismo criterio con que edita el comentario del analista), aunque sea Empresa Gestora.
    if (
      esAnalistaSeguridad &&
      (solicitudSelected.idTipoEstado === PRE_APROBADA_STATUS_ID ||
        solicitudSelected.idTipoEstado === APROBADA_RT_STATUS_ID)
    ) {
      return isEdit;
    }

    // Estados donde Empresa Gestora no puede editar
    const restrictedStatesEmpresaGestora = [
      REVISION_STATUS_ID,
      PRE_APROBADA_STATUS_ID,
      APROBADA_RT_STATUS_ID,
      APROBADA_STATUS_ID, // Agregado: Empresa Gestora no puede editar cuando está APROBADA
    ];

    // Estados donde Contratista no puede editar
    // CU55: el Contratista edita el AR en EN_PROCESO/OBSERVADA/APROBADA/ACTUALIZACION
    const restrictedStatesContratista: number[] = [];

    if (
      isEmpresaGestora &&
      restrictedStatesEmpresaGestora.includes(
        solicitudSelected.idTipoEstado
      )
    ) {
      return false;
    }

    if (
      isContratista &&
      restrictedStatesContratista.includes(
        solicitudSelected.idTipoEstado
      )
    ) {
      return false;
    }

    return isEdit;
  }, [
    isEmpresaGestora,
    isContratista,
    esAnalistaSeguridad,
    solicitudSelected.idTipoEstado,
    isEdit,
  ]);

  const [isEditing, setIsEditing] = useState(canEditAnalisisRiesgo);

  // CU55: cuando el Analista de Seguridad (Empresa Gestora) puede editar el A.R.,
  // la solicitud está en un estado read-only (isDisabled=true por isFormDisabled),
  // pero esa restricción no debe aplicar a los controles del A.R. (agregar fase/peligro,
  // descripción de fase, Si/No de peligros de entorno). Se anula isDisabled en ese caso.
  const isDisabledEfectivo = isDisabled && !canEditAnalisisRiesgo;

  // Función para cargar solicitudes aprobadas
  const cargarSolicitudesAprobadas = useCallback(async () => {
    try {
      setLoadingSolicitudes(true);

      // Obtener el ID de la solicitud actual
      const solicitudId = solicitudSelected.id;

      if (!solicitudId) {
        logger.warn(
          'No se encontró ID de solicitud en la solicitud seleccionada'
        );
        return;
      }

      // Construir URL con el parámetro solicitudID
      const url = `solicitudes/aprobadas?solicitudID=${solicitudId}`;
      const response = await findAll(url);

      if (response.data && Array.isArray(response.data)) {
        setSolicitudesAprobadas(response.data);
        logger.log('Solicitudes aprobadas cargadas, cantidad:', response.data?.length);
      } else {
        logger.warn('Respuesta inesperada del backend, status:', response?.status);
        setSolicitudesAprobadas([]);
      }
    } catch (error) {
      logger.error('Error al cargar solicitudes aprobadas:', logger.safeError(error));
      toast.error(
        'Error al cargar las solicitudes disponibles para copiar'
      );
      setSolicitudesAprobadas([]);
    } finally {
      setLoadingSolicitudes(false);
    }
  }, [solicitudSelected.id]);

  // Cargar solicitudes aprobadas cuando se monte el componente y haya datos de solicitud
  useEffect(() => {
    if (solicitudSelected.id) {
      cargarSolicitudesAprobadas();
    }
  }, [solicitudSelected.id, cargarSolicitudesAprobadas]);

  const handleClick = async (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (cargarAnalisisRiesgo) {
      try {
        // Cargar los datos del análisis de riesgo desde el backend
        const datosAnalisisRiesgo =
          await cargarAnalisisRiesgoCompleto(
            solicitudSelected.id // Usar ID de la solicitud seleccionada o 1 por defecto
          );

        // Actualizar los estados locales con los datos cargados
        if (datosAnalisisRiesgo.peligroCheckStatesEntorno) {
          setPeligroCheckStatesEntorno(
            datosAnalisisRiesgo.peligroCheckStatesEntorno
          );
        }

        if (datosAnalisisRiesgo.observaciones) {
          setObservaciones(datosAnalisisRiesgo.observaciones);
        }

        if (datosAnalisisRiesgo.comentarioAnalistaSeguridad) {
          setComentarioAnalistaSeguridad(
            datosAnalisisRiesgo.comentarioAnalistaSeguridad
          );
        }
      } catch (error) {
        toast.error(
          'Error al cargar los datos del análisis de riesgo'
        );
      }

      setCargarAnalisisRiesgo(false);
      setIsEditing(canEditAnalisisRiesgo);
    } else {
      logger.log('Los datos ya fueron cargados previamente');
    }
  };

  const handleGuardar = async () => {
    try {
      setIsGuardando(true);

      if (!solicitudSelected.id) {
        toast.error('No se ha seleccionado una solicitud');
        return;
      }

      // 1. Validar que las fases personales tengan al menos un peligro
      if (!fasesPersonales || fasesPersonales.length === 0) {
        logger.log('ERROR: No hay fases personales');
        toast.error(
          'Debe agregar al menos una fase en la evaluación de riesgos personales'
        );
        return;
      }

      let todasLasFasesPersonalesTienenPeligros = true;
      let todosLosPeligrosPersonalesTienenValores = true;

      logger.log('=== VALIDANDO FASES PERSONALES ===');
      for (const fase of fasesPersonales) {
        logger.log(`Fase ${fase.id}:`, {
          descripcion: fase.descripcion,
          cantidadPeligros: fase.peligros?.length || 0,
          evaluacionRiesgo: fase.evaluacionRiesgo,
        });

        // Verificar que la fase tenga peligros
        if (!fase.peligros || fase.peligros.length === 0) {
          logger.log(`ERROR: Fase ${fase.id} no tiene peligros`);
          todasLasFasesPersonalesTienenPeligros = false;
          break;
        }

        logger.log(
          `Fase ${fase.id} tiene peligros:`,
          fase.peligros.length
        );

        // Validar que la fase tenga valores de exposición, probabilidad y consecuencia
        if (
          !fase.evaluacionRiesgo ||
          !fase.evaluacionRiesgo.exposicion ||
          !fase.evaluacionRiesgo.probabilidad ||
          !fase.evaluacionRiesgo.consecuencia
        ) {
          logger.log(`Fase ${fase.id} tiene valores incompletos:`, {
            exposicion: fase.evaluacionRiesgo?.exposicion,
            probabilidad: fase.evaluacionRiesgo?.probabilidad,
            consecuencia: fase.evaluacionRiesgo?.consecuencia,
          });
          todosLosPeligrosPersonalesTienenValores = false;
          break;
        }
      }

      logger.log('Resultado validación personales:', {
        todasLasFasesPersonalesTienenPeligros,
        todosLosPeligrosPersonalesTienenValores,
      });

      if (!todasLasFasesPersonalesTienenPeligros) {
        logger.log('ERROR: Hay fases sin peligros');
        toast.error(
          'Todas las fases de evaluación de riesgos personales deben tener al menos un peligro agregado'
        );
        return;
      }

      if (!todosLosPeligrosPersonalesTienenValores) {
        logger.log('ERROR: Valores incompletos en fases personales');
        toast.error(
          'Todas las fases personales con peligros deben tener valores de exposición, probabilidad y consecuencia completados'
        );
        return;
      }

      // 2. Validar que las fases de entorno tengan al menos un peligro
      logger.log('=== VALIDANDO FASES ENTORNO ===');
      if (!fasesEntorno || fasesEntorno.length === 0) {
        logger.log('ERROR: No hay fases de entorno');
        toast.error(
          'Debe agregar al menos una fase en la identificación de peligros del entorno'
        );
        return;
      }

      let todasLasFasesEntornoTienenPeligros = true;
      for (const fase of fasesEntorno) {
        logger.log(`Fase entorno ${fase.id}:`, {
          descripcion: fase.descripcion,
          cantidadPeligros: fase.peligros?.length || 0,
        });

        // Verificar que la fase tenga peligros
        if (!fase.peligros || fase.peligros.length === 0) {
          logger.log(
            `ERROR: Fase entorno ${fase.id} no tiene peligros`
          );
          todasLasFasesEntornoTienenPeligros = false;
          break;
        }

        logger.log(
          `Fase entorno ${fase.id} tiene peligros:`,
          fase.peligros.length
        );
      }

      logger.log(
        'todasLasFasesEntornoTienenPeligros:',
        todasLasFasesEntornoTienenPeligros
      );

      if (!todasLasFasesEntornoTienenPeligros) {
        logger.log('ERROR: Hay fases de entorno sin peligros');
        toast.error(
          'Todas las fases de identificación de peligros del entorno deben tener al menos un peligro agregado'
        );
        return;
      }

      // 3. Validar que los peligros marcados como "SI" en entorno tengan todas las barreras completas
      logger.log('=== DEBUG VALIDACION ENTORNO ===');
      logger.log(
        'validateEntornoRef.current función existe:',
        !!validateEntornoRef.current
      );
      logger.log(
        'tipo de validateEntornoRef.current:',
        typeof validateEntornoRef.current
      );

      if (validateEntornoRef.current) {
        logger.log('Ejecutando validación de entorno...');
        const validacionExitosa = validateEntornoRef.current();
        logger.log(
          'Resultado de validación entorno:',
          validacionExitosa
        );

        if (!validacionExitosa) {
          logger.log('Validación falló, mostrando toast de error');
          toast.error(
            'Por favor complete todas las barreras de mitigación (física, administrativa y EPP específico) para los peligros seleccionados como "SÍ" en la identificación de peligros del entorno.'
          );
          return;
        }
      } else {
        logger.log('validateEntornoRef.current no está disponible');
      }

      await guardarAnalisisRiesgo(
        solicitudSelected.id,
        peligroCheckStatesEntorno,
        observaciones
      );

      toast.success('Análisis de riesgo guardado exitosamente');

      // Refrescar la solicitud para reflejar la transición de estado
      // (APROBADA → ACTUALIZACIÓN) sin necesidad de recargar la página
      await buscarSolicitud(String(solicitudSelected.id));

      // Recargar el AR desde el backend para reflejar el último usuario que
      // editó cada peligro (usuarioUltimaEdicion) sin necesidad de refrescar
      // la página. cargarAnalisisRiesgoCompleto ya actualiza
      // fasesPersonales/fasesEntorno internamente; aquí solo re-sincronizamos
      // los estados que quedan en el componente.
      const datosActualizados = await cargarAnalisisRiesgoCompleto(
        solicitudSelected.id
      );
      if (datosActualizados.peligroCheckStatesEntorno) {
        setPeligroCheckStatesEntorno(
          datosActualizados.peligroCheckStatesEntorno
        );
      }
    } catch (error) {
      logger.error('Error al guardar análisis de riesgo:', logger.safeError(error));
      toast.error('Error al guardar el análisis de riesgo');
    } finally {
      setIsGuardando(false);
    }
  };

  const handleGuardarComentario = async () => {
    try {
      setIsGuardandoComentario(true);

      if (!solicitudSelected.id) {
        toast.error('No se ha seleccionado una solicitud');
        return;
      }

      await update('faseAr/comentarioAnalista', {
        id: solicitudSelected.id,
        comentario: comentarioAnalistaSeguridad,
      });

      toast.success('Comentario guardado exitosamente');
    } catch (error) {
      logger.error(
        'Error al guardar el comentario del Analista:',
        logger.safeError(error)
      );
      toast.error('Error al guardar el comentario');
    } finally {
      setIsGuardandoComentario(false);
    }
  };

  const handleCancelar = () => {
    // Aquí puedes agregar lógica para resetear los formularios o redirigir
    toast.info('Operación cancelada');
  };

  const handleCopiarAnalisisRiesgo = async () => {
    if (!solicitudSeleccionada) {
      toast.warning('Por favor seleccione una solicitud para copiar');
      return;
    }

    try {
      // El valor del select ahora es directamente el ID numérico
      const idSolicitudACopiar = parseInt(solicitudSeleccionada);

      if (isNaN(idSolicitudACopiar)) {
        toast.error('ID de solicitud inválido');
        return;
      }

      // Buscar la solicitud seleccionada para mostrar su número en el mensaje
      const solicitudInfo = solicitudesAprobadas.find(
        (s) => s.id === idSolicitudACopiar
      );
      const numeroSolicitud = solicitudInfo
        ? solicitudInfo.numeroSolicitud
        : `ID: ${idSolicitudACopiar}`;

      // Cargar los datos del análisis de riesgo de la solicitud seleccionada
      const datosAnalisisRiesgo = await cargarAnalisisRiesgoCompleto(
        idSolicitudACopiar
      );

      // Actualizar los estados locales con los datos copiados
      if (datosAnalisisRiesgo.peligroCheckStatesEntorno) {
        setPeligroCheckStatesEntorno(
          datosAnalisisRiesgo.peligroCheckStatesEntorno
        );
      }

      if (datosAnalisisRiesgo.observaciones) {
        setObservaciones(datosAnalisisRiesgo.observaciones);
      }

      toast.success(
        `Análisis de riesgo copiado de la solicitud: ${numeroSolicitud}`
      );
    } catch (error) {
      logger.error('Error al copiar análisis de riesgo:', logger.safeError(error));
      toast.error('Error al copiar el análisis de riesgo');
    }
  };

  return (
    <>
      {/* Botón temporal para probar carga de datos - FUERA del accordion */}

      <Accordion
        id={'AnalisisRiesgo'}
        title="Análisis de Riesgo"
        collapsed={true}
        handleClick={handleClick}
      >
        {/* Mensaje informativo sobre EPP */}

        <div className="alert alert-info mb-4 mt-3">
          <i
            className="fa fa-info-circle me-2"
            aria-hidden="true"
          ></i>
          <strong>Información importante:</strong> El uso de los
          elementos de protección personal (EPP) mínimos es
          obligatorio dentro del ámbito de la obra. En este formulario
          se deben agregar únicamente los EPP específicos requeridos
          para la tarea a realizar.
        </div>
        <div>
          {/* Sección para copiar A.R. de Solicitud */}
          <div className="d-flex justify-content-center mt-4 mb-5">
            <div className="d-flex align-items-center gap-3">
              <span>Copiar A.R. de Solicitud</span>
              <Form.Select
                value={solicitudSeleccionada}
                onChange={(e) =>
                  setSolicitudSeleccionada(e.target.value)
                }
                style={{ width: '220px' }}
                disabled={
                  isDisabledEfectivo || !isEditing || loadingSolicitudes
                }
              >
                <option value="">
                  {loadingSolicitudes ? 'Cargando...' : 'Seleccionar'}
                </option>
                {solicitudesAprobadas.map((solicitud) => (
                  <option
                    key={solicitud.id}
                    value={solicitud.id.toString()}
                  >
                    {solicitud.numeroSolicitud}
                  </option>
                ))}
              </Form.Select>
              <Button
                variant="primary"
                onClick={handleCopiarAnalisisRiesgo}
                disabled={
                  isDisabledEfectivo || !isEditing || !solicitudSeleccionada
                }
              >
                Copiar
              </Button>
            </div>
          </div>

          <EvaluacionRiesgosPersonales
            isDisabled={isDisabledEfectivo}
            isEditing={isEditing}
            fasesPersonales={fasesPersonales}
            agregarFase={agregarFase}
            editarFase={editarFase}
            agregarPeligro={agregarPeligro}
            editarPeligro={editarPeligro}
            eliminarPeligroById={eliminarPeligroById}
            handlePeligroSelected={handlePeligroSelected}
            peligroSelected={peligroSelected}
            actualizarRiesgoEvaluacion={actualizarRiesgoEvaluacion}
            eliminarFase={eliminarFase}
          />
          <IdentificacionPeligrosEntorno
            isDisabled={isDisabledEfectivo}
            isEditing={isEditing}
            peligroCheckStates={peligroCheckStatesEntorno}
            setPeligroCheckStates={setPeligroCheckStatesEntorno}
            observaciones={observaciones}
            setObservaciones={setObservaciones}
            fasesEntorno={fasesEntorno}
            setFasesEntorno={setFasesEntorno}
            agregarFase={agregarFase}
            editarFase={editarFase}
            handlePeligroSelected={handlePeligroSelected}
            peligroSelected={peligroSelected}
            eliminarFase={eliminarFase}
            onValidationRef={setValidateEntorno}
          />

          {/* Comentarios del Analista de Seguridad (CU55) */}
          <div className="mt-4">
            <div className="observaciones-container p-3">
              <h6 className="mb-3">
                Comentarios del Analista de Seguridad
              </h6>
              <div className="observaciones-text-area">
                <textarea
                  className="form-control"
                  rows={4}
                  maxLength={500}
                  placeholder="Comentarios y aclaraciones del Analista de Seguridad..."
                  value={comentarioAnalistaSeguridad}
                  onChange={(e) =>
                    setComentarioAnalistaSeguridad(e.target.value)
                  }
                  disabled={!canEditComentarioAnalista}
                />
              </div>
              {canEditComentarioAnalista && (
                <div className="d-flex justify-content-end mt-2">
                  <Button
                    variant="primary"
                    onClick={handleGuardarComentario}
                    disabled={isGuardandoComentario}
                  >
                    {isGuardandoComentario
                      ? 'Guardando...'
                      : 'Guardar Comentario'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Botones de acción */}
          {isEditing && !isDisabledEfectivo && (
            <div className="d-flex justify-content-center gap-3 mt-4 p-3">
              <Button
                variant="secondary"
                onClick={handleCancelar}
                disabled={isGuardando}
              >
                Cancelar
              </Button>

              <Button
                variant="primary"
                onClick={handleGuardar}
                disabled={isGuardando}
              >
                {isGuardando ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          )}
        </div>
      </Accordion>
    </>
  );
};

export default AnalisisRiesgo;
